import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash, FloppyDisk, X } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { api } from "../../lib/kostra";
import { useAuth } from "../../lib/auth";

export default function ManageCustomIngredientsDialog({ trigger, onChange }) {
  const { isAdmin, openLogin } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: "", category: "primary", rate_per_kg: "", unit: "kg" });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/ingredients/custom");
      setItems(data || []);
    } catch (e) {
      toast.error("Failed to load custom ingredients");
    }
  };

  useEffect(() => { if (open && isAdmin) refresh(); }, [open, isAdmin]);

  const handleOpenChange = (v) => {
    if (v && !isAdmin) {
      openLogin();
      return;
    }
    setOpen(v);
    if (!v) {
      setEditingId(null);
      setDraft({ name: "", category: "primary", rate_per_kg: "", unit: "kg" });
    }
  };

  const beginEdit = (it) => {
    setEditingId(it.id);
    setDraft({
      name: it.name || "",
      category: it.category || "primary",
      rate_per_kg: String(it.rate_per_kg ?? ""),
      unit: it.unit || "kg",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setDraft({ name: "", category: "primary", rate_per_kg: "", unit: "kg" });
  };

  const saveDraft = async () => {
    const name = draft.name.trim();
    const rate = parseFloat(draft.rate_per_kg);
    if (!name) { toast.error("Name is required"); return; }
    if (!Number.isFinite(rate) || rate < 0) { toast.error("Enter a valid non-negative rate"); return; }
    setBusy(true);
    try {
      const payload = { name, category: draft.category, rate_per_kg: rate, unit: draft.unit || "kg" };
      if (editingId) {
        await api.put(`/ingredients/custom/${editingId}`, payload);
        toast.success("Ingredient updated");
      } else {
        await api.post("/ingredients/custom", { ...payload, tags: ["custom"] });
        toast.success("Ingredient added");
      }
      cancelEdit();
      await refresh();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/ingredients/custom/${it.id}`);
      toast.success("Ingredient deleted");
      if (editingId === it.id) cancelEdit();
      await refresh();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl" data-testid="manage-custom-ingredients-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0A3B24]">Manage Custom Ingredients</DialogTitle>
          <p className="text-xs text-[#4B5563] mt-1">
            Add private ingredients that aren't in KOSTRA's curated list. They show up across the
            calculator with the "custom" tag and can be edited or deleted any time.
          </p>
        </DialogHeader>

        {/* Editor */}
        <div className="rounded-xl border border-[#E5E7EB] bg-[#FDF9EE] p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4">
            <Label className="kostra-data-label mb-1 block">Name</Label>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g., Vanilla Bean Paste"
              className="h-10"
              data-testid="custom-ing-name"
            />
          </div>
          <div className="md:col-span-3">
            <Label className="kostra-data-label mb-1 block">Category</Label>
            <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
              <SelectTrigger className="h-10" data-testid="custom-ing-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="primary">Primary</SelectItem>
                <SelectItem value="secondary">Secondary</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label className="kostra-data-label mb-1 block">Rate (₹/kg)</Label>
            <Input
              type="number" min="0" step="1"
              value={draft.rate_per_kg}
              onChange={(e) => setDraft({ ...draft, rate_per_kg: e.target.value })}
              className="h-10 font-mono-num"
              data-testid="custom-ing-rate"
            />
          </div>
          <div className="md:col-span-2 flex items-end gap-2">
            <Button
              onClick={saveDraft}
              disabled={busy}
              className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold flex-1 h-10"
              data-testid="custom-ing-save"
            >
              <FloppyDisk size={14} weight="bold" className="mr-1.5" />
              {editingId ? "Update" : "Add"}
            </Button>
            {editingId && (
              <button
                type="button"
                onClick={cancelEdit}
                className="h-10 w-10 rounded-md border border-[#E5E7EB] text-[#4B5563] hover:bg-white inline-flex items-center justify-center"
                aria-label="Cancel edit"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="rounded-xl border border-[#E5E7EB] bg-white max-h-[400px] overflow-y-auto" data-testid="custom-ing-list">
          {items.length === 0 ? (
            <div className="py-10 text-center text-sm text-[#4B5563]">
              No custom ingredients yet. Add one above to get started.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563]">
                  <th className="py-2.5 px-3">Name</th>
                  <th className="py-2.5 px-3">Category</th>
                  <th className="py-2.5 px-3 text-right">Rate (₹/kg)</th>
                  <th className="py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}
                      className={`border-b border-[#F1F2F4] hover:bg-[#FDF9EE]/40 ${editingId === it.id ? "bg-[#FDF9EE]" : ""}`}
                      data-testid={`custom-ing-row-${it.id}`}>
                    <td className="py-2.5 px-3 font-medium text-[#111827]">{it.name}</td>
                    <td className="py-2.5 px-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full
                        ${it.category === "primary" ? "bg-[#0A3B24]/10 text-[#0A3B24]" : "bg-[#D4AF37]/15 text-[#A87C0E]"}`}>
                        {it.category}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono-num text-[#0A3B24] font-semibold">
                      ₹ {Number(it.rate_per_kg || 0).toLocaleString("en-IN")}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          type="button"
                          onClick={() => beginEdit(it)}
                          className="h-8 w-8 rounded-md text-[#4B5563] hover:bg-[#0A3B24]/10 hover:text-[#0A3B24] inline-flex items-center justify-center"
                          aria-label={`Edit ${it.name}`}
                          data-testid={`custom-ing-edit-${it.id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(it)}
                          className="h-8 w-8 rounded-md text-[#4B5563] hover:bg-[#FBE8E8] hover:text-[#DC2626] inline-flex items-center justify-center"
                          aria-label={`Delete ${it.name}`}
                          data-testid={`custom-ing-delete-${it.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
